import { useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import SectionHeading from "@/components/feature/SectionHeading";
import VideoCard from "@/components/feature/VideoCard";
import Pagination from "@/components/feature/Pagination";
import { categoryMeta, regionOptions, videos, yearOptions, type VideoCategory } from "@/mocks/videos";
import { usePageMeta } from "@/hooks/usePageMeta";

const PAGE_SIZE = 8;
const sortOptions = [
  { value: "latest", label: "最新上线" },
  { value: "hot", label: "最受欢迎" },
];

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const categoryKey = (type && type in categoryMeta ? type : "movie") as VideoCategory;
  const meta = categoryMeta[categoryKey];

  const [year, setYear] = useState("全部");
  const [region, setRegion] = useState("全部");
  const [sort, setSort] = useState("latest");
  const [page, setPage] = useState(1);

  usePageMeta({
    title: `${meta.label}分类 - 高清视频免费观看`,
    description: `高清视频免费观看提供丰富${meta.label}资源，最新影视在线看。${meta.intro}`,
    keywords: `${meta.label},高清${meta.label},${meta.label}在线观看,高清视频免费观看`,
  });

  const filtered = useMemo(() => {
    const list = videos.filter((v) => {
      if (v.category !== categoryKey) return false;
      if (year !== "全部" && String(v.year) !== year) return false;
      if (region !== "全部" && v.region !== region) return false;
      return true;
    });
    return [...list].sort((a, b) =>
      sort === "hot" ? b.rating - a.rating : b.publishDate.localeCompare(a.publishDate)
    );
  }, [categoryKey, year, region, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const pageItems = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const resetFilters = () => {
    setYear("全部");
    setRegion("全部");
    setSort("latest");
    setPage(1);
  };

  const onFilterChange = (setter: (v: string) => void) => (value: string) => {
    setter(value);
    setPage(1);
  };

  const selectClass =
    "px-3 py-2 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-800 outline-none focus:border-primary-400 cursor-pointer";

  return (
    <div className="bg-background-100">
      <div className="bg-background-50 border-b border-background-200">
        <div className="max-w-[1360px] mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-10">
          <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: meta.label }]} />
          <h1 className="font-heading font-extrabold text-2xl md:text-4xl text-foreground-950 mt-4">{meta.label}分类</h1>
          <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">{meta.intro}</p>
        </div>
      </div>

      <div className="max-w-[1360px] mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-10">
        {/* Filters */}
        <div className="rounded-lg bg-background-50 border border-background-200 p-4 md:p-5">
          <div className="flex flex-wrap items-center gap-3 md:gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-foreground-500 whitespace-nowrap">年份</span>
              <select value={year} onChange={(e) => onFilterChange(setYear)(e.target.value)} className={selectClass}>
                {yearOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt === "全部" ? "全部年份" : `${opt}年`}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-foreground-500 whitespace-nowrap">地区</span>
              <select value={region} onChange={(e) => onFilterChange(setRegion)(e.target.value)} className={selectClass}>
                {regionOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt === "全部" ? "全部地区" : opt}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-foreground-500 whitespace-nowrap">排序</span>
              <select value={sort} onChange={(e) => onFilterChange(setSort)(e.target.value)} className={selectClass}>
                {sortOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={resetFilters}
              className="ml-auto inline-flex items-center gap-1 px-4 py-2 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-400 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i>
              重置筛选
            </button>
          </div>
        </div>

        <div className="mt-6 md:mt-8">
          <SectionHeading
            icon="ri-list-check-2"
            title={`共找到 ${filtered.length} 部${meta.label}`}
            subtitle={`当前筛选：${year === "全部" ? "全部年份" : `${year}年`} · ${region === "全部" ? "全部地区" : region}`}
          />
        </div>

        {pageItems.length > 0 ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
              {pageItems.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
            <div className="mt-8 md:mt-10">
              <Pagination currentPage={current} totalPages={totalPages} onChange={setPage} />
            </div>
          </>
        ) : (
          <div className="rounded-lg bg-background-50 border border-background-200 py-16 text-center">
            <span className="w-16 h-16 mx-auto rounded-full bg-secondary-100 text-secondary-600 flex items-center justify-center">
              <i className="ri-search-eye-line text-3xl"></i>
            </span>
            <h3 className="font-heading font-bold text-lg text-foreground-900 mt-4">没有符合条件的{meta.label}</h3>
            <p className="text-sm text-foreground-500 mt-2">换个年份或地区试试，或者重置筛选查看全部内容。</p>
            <button
              type="button"
              onClick={resetFilters}
              className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i>
              重置筛选
            </button>
          </div>
        )}

        <div className="mt-10">
          <Link to="/" className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700">
            <i className="ri-arrow-left-line"></i>
            返回首页
          </Link>
        </div>
      </div>
    </div>
  );
}